import React, { useState, useEffect, useCallback, useRef } from "react";
import {
  Nav,
  NavItem,
  NavLink,
  TabContent,
  TabPane,
  Row,
  Col
} from "reactstrap";
import HeaderDashboard from "../../components/layout/header/HeaderDashboard";
import Profile from "../../components/profile/Profile";
import Password from "../../components/password/Password";
import LoginAsUser from "../../components/loginasuser/LoginAsUser";
import AccountingSoftwareTab from "../../components/accountingsoftware/AccountingSoftwareTab";
import useAccountingSoftwareApi from "../../hooks/useAccountingSoftwareApi";
import "./../../assets/scss/account.scss";
import "./../../components/all-account-steps/allaccountsteps.scss";
import { myAccount, responseStatus } from "../../locales/en";
import { useDispatch, useSelector } from "react-redux";
import { fetchLegalDocumentDataThunk } from "../../redux/ownerTabSlice";
import UploadDocument from "./UploadDocument";
import { getAccountUploadDocumentsUrl } from "../../utils/constants/apiEndPoints";
import { showToast } from "../../utils/utils";
import {
  INITIATED,
  showToastType,
  STATUS_1
} from "../../utils/constants/appConstants";
import { useLocation, useNavigate } from "react-router-dom";
import publicAxios from "../../utils/publicAxios";
import { LOGIN, UPLOAD_DOCUMENTS } from "../../route/paths";
import { getOnboardingData } from "../../redux/loginSlice";

const Account = () => {
  const dispatch = useDispatch();
  const [activeTab, setActiveTab] = useState(STATUS_1);
  const initialRender = useRef(true);
  const location = useLocation();
  const navigate = useNavigate();
  const params = new URLSearchParams(location.search);
  const token = params.get("token");
  const [isTokenFlowCompleted, setIsTokenFlowCompleted] = useState(false);
  const [isTokenValid, setIsTokenValid] = useState(false);
  const [onboardingStatus, setOnboardingStatus] = useState(null);

  const { GetConnectedSoftware, DisconnectSoftware } =
    useAccountingSoftwareApi();
  const [softwareDetails, setSoftwareDetails] = useState({});

  const { profile: loggedUserData } = useSelector(
    (state) => state.userManagement
  );

  useEffect(() => {
    if (location.pathname === UPLOAD_DOCUMENTS && !token) {
      window.location.href = LOGIN;
    }
  }, [location.pathname, token]);
  const CheckSoftwareDetails = useCallback(async () => {
    try {
      const response = await GetConnectedSoftware();
      setSoftwareDetails(response.data.data);
    } catch (error) {
      showToast(
        error.response.data.message || responseStatus.errorText,
        showToastType.error
      );
    }
  }, [GetConnectedSoftware]);

  const getPrimaryUserId = () => {
    try {
      const userData = loggedUserData;
      if (!userData) return null;

      return userData?.primaryUserId || null;
    } catch {
      return null;
    }
  };

  const primaryUserId = getPrimaryUserId();
  const userData = loggedUserData;
  const userAccountId = loggedUserData?.userAccountMeta?.userAccountId;

  const [isDocumentUploadPending, setIsDocumentUploadPending] = useState(false);
  const fetchLegalDocumentData = async () => {
    try {
      const response = await dispatch(
        fetchLegalDocumentDataThunk(Number(userAccountId))
      ).unwrap();
      const filteredData = response.data.data.filter((item) => !item.deletedAt);
      const showTab =
        filteredData.annaturEventData.event !== "envelope_completed";
      setIsDocumentUploadPending(showTab);
    } catch (error) {
      if (error && error.status === 404) {
        setIsDocumentUploadPending(false);
      }
    }
  };

  useEffect(() => {
    if (!isTokenFlowCompleted || !isTokenValid) return;
    if (userAccountId) fetchLegalDocumentData();
  }, [
    userAccountId,
    isTokenFlowCompleted,
    isTokenValid,
    fetchLegalDocumentData
  ]);

  useEffect(() => {
    if (!isTokenFlowCompleted || !isTokenValid) return;
    if (initialRender.current) {
      CheckSoftwareDetails();
      initialRender.current = false;
    }
  }, [CheckSoftwareDetails, isTokenFlowCompleted, isTokenValid]);

  useEffect(() => {
    const runTokenFlow = async () => {
      if (!token) {
        setIsTokenValid(true);
        setIsTokenFlowCompleted(true);
        return;
      }

      try {
        const url = getAccountUploadDocumentsUrl(token);
        await publicAxios.get(url);
        setIsTokenValid(true);
        setActiveTab("5");
      } catch {
        setIsTokenValid(false);
        showToast(showToastType.errorText, showToastType.error);
        navigate(LOGIN);
      } finally {
        setIsTokenFlowCompleted(true);
      }
    };

    runTokenFlow();
  }, [token]);

  useEffect(() => {
    if (!isTokenFlowCompleted || !isTokenValid) return;

    const handleGetOnboardingData = async () => {
      const response = await dispatch(getOnboardingData()).unwrap();
      setOnboardingStatus(response?.data?.data?.onboardingStatus);
    };

    handleGetOnboardingData();
  }, [isTokenFlowCompleted, isTokenValid]);

  const isReady = isTokenFlowCompleted && isTokenValid;

  if (!isReady) {
    return (
      <div className="spinner-wrap bg-light loader_absolute">
        <div className="spinner-border custom-spinner-color" />
      </div>
    );
  }

  if (!userData) {
    return null;
  }

  return (
    <>
      <HeaderDashboard />

      <LoginAsUser />
      <div className="home-main-section">
        <div className="container">
          <div className="home-title">
            <h1>{myAccount?.main_title}</h1>
          </div>
        </div>
      </div>

      <div className="account-page-wrap">
        <div className="profile-tab-wrap">
          <div className="container">
            <div className="row justify-content-center">
              <div className="col-md-10">
                <Nav tabs className="profile-nav custom-nav-accountpage">
                  <NavItem>
                    <NavLink
                      className={activeTab === "1" ? "active" : ""}
                      onClick={() => setActiveTab("1")}
                    >
                      {myAccount?.tabNames?.profile}
                    </NavLink>
                  </NavItem>
                  <NavItem>
                    <NavLink
                      className={activeTab === "2" ? "active" : ""}
                      onClick={() => setActiveTab("2")}
                    >
                      {myAccount?.tabNames?.password}
                    </NavLink>
                  </NavItem>

                  {!primaryUserId ? (
                    <NavItem>
                      <NavLink
                        className={activeTab === "4" ? "active" : ""}
                        onClick={() => setActiveTab("4")}
                      >
                        {myAccount?.tabNames?.accSoftware}
                      </NavLink>
                    </NavItem>
                  ) : (
                    softwareDetails?.isConnected === true && (
                      <NavItem>
                        <NavLink
                          className={activeTab === "4" ? "active" : ""}
                          onClick={() => setActiveTab("4")}
                        >
                          {myAccount?.tabNames?.accSoftware}
                        </NavLink>
                      </NavItem>
                    )
                  )}

                  {!isDocumentUploadPending &&
                    onboardingStatus === INITIATED && (
                      <NavItem>
                        <NavLink
                          className={activeTab === "5" ? "active" : ""}
                          onClick={() => setActiveTab("5")}
                        >
                          {myAccount?.tabNames?.uploadDocuments}
                        </NavLink>
                      </NavItem>
                    )}
                </Nav>

                <TabContent activeTab={activeTab} id="profile-tabcontent">
                  <TabPane tabId="1">
                    <Row>
                      <Col sm="12">
                        <Profile />
                      </Col>
                    </Row>
                  </TabPane>
                  <TabPane tabId="2">
                    <Row>
                      <Col sm="12">
                        <Password />
                      </Col>
                    </Row>
                  </TabPane>

                  {!primaryUserId ? (
                    <TabPane tabId="4">
                      <Row>
                        <Col sm="12">
                          <AccountingSoftwareTab
                            primaryUserId={primaryUserId}
                            DisconnectSoftware={DisconnectSoftware}
                            softwareDetails={softwareDetails}
                            CheckSoftwareDetails={CheckSoftwareDetails}
                          />
                        </Col>
                      </Row>
                    </TabPane>
                  ) : (
                    softwareDetails?.isConnected === true && (
                      <TabPane tabId="4">
                        <Row>
                          <Col sm="12">
                            <AccountingSoftwareTab
                              primaryUserId={primaryUserId}
                              DisconnectSoftware={DisconnectSoftware}
                              softwareDetails={softwareDetails}
                              CheckSoftwareDetails={CheckSoftwareDetails}
                            />
                          </Col>
                        </Row>
                      </TabPane>
                    )
                  )}

                  {!isDocumentUploadPending &&
                    onboardingStatus === INITIATED && (
                      <TabPane tabId="5">
                        <Row>
                          <Col sm="12">
                            <UploadDocument
                              onboardingStatus={onboardingStatus}
                            />
                          </Col>
                        </Row>
                      </TabPane>
                    )}
                </TabContent>
              </div>
            </div>
          </div>
        </div>
      </div>
    </>
  );
};

export default Account;
